import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Lock,
  Search,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";

import { TREND_REPORT_TYPE } from "../api/trends";
import { getVisibilityReport, linkVisibilityReport } from "../api/visibility";
import { isLoggedIn } from "../utils/jwtUtils";
import { LLM_COLORS } from "../utils/llmColors";
import { capitalize, pluralize } from "../utils/text";
import CrossReportBanner from "./CrossReportBanner";
import DownloadPdfButton from "./DownloadPdfButton";
import LoadingSpinner from "./LoadingSpinner";
import VisibilityAttributionBadge from "./VisibilityAttributionBadge";
import VisibilityWhyPanel from "./VisibilityWhyPanel";

/* ── Helpers ───────────────────────────────────────────────────────────── */

const scoreColor = (score) => {
  if (score >= 80) return "bg-emerald-700";
  if (score >= 60) return "bg-emerald-500";
  if (score >= 50) return "bg-yellow-500";
  return "bg-orange-500";
};

const scoreTextColor = (score) => {
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-emerald-400";
  if (score >= 50) return "text-yellow-400";
  return "text-orange-400";
};

const SCORE_LEGEND = [
  {
    range: "80-100",
    label: "Topper",
    desc: "You have won the LLM Battle. Your business is getting recommended a lot and you are high in the list!",
    color: "bg-emerald-700",
  },
  {
    range: "60-80",
    label: "Excellent",
    desc: "A lot of LLMs are giving your name. This is more than enough to get a lot of traction",
    color: "bg-emerald-500",
  },
  {
    range: "50-60",
    label: "Amazing",
    desc: "You are mentioned! This means you are among the top results for this query",
    color: "bg-yellow-500",
  },
  {
    range: "0-50",
    label: "Poor",
    desc: "Not appearing in LLM search results. Don't worry, only a handful of businesses appear in AI search",
    color: "bg-orange-500",
  },
];

/* ── Component ─────────────────────────────────────────────────────────── */

const VisibilityReport = () => {
  const { visibility_report_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Overview");
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState({});

  const loggedIn = isLoggedIn();

  // Check auth
  useEffect(() => {
    const handleAuthChange = async () => {
      // Link report on login
      if (isLoggedIn() && visibility_report_id) {
        try {
          const res = await linkVisibilityReport(visibility_report_id);
          setReportData(res.data);
        } catch {
          // If link fails, just refetch
          try {
            const res = await getVisibilityReport(visibility_report_id);
            setReportData(res.data);
          } catch {
            // ignore
          }
        }
      }
    };

    window.addEventListener("authChange", handleAuthChange);
    return () => window.removeEventListener("authChange", handleAuthChange);
  }, [visibility_report_id]);

  // Load data
  useEffect(() => {
    const stateData = location.state?.visibilityResponse;
    const alreadyLoggedIn = isLoggedIn();

    // If we came from navigation with state data AND user is already logged in,
    // call link to get the full report (state data may be anonymous/truncated)
    if (stateData && alreadyLoggedIn) {
      const linkAndLoad = async () => {
        setLoading(true);
        try {
          const res = await linkVisibilityReport(visibility_report_id);
          setReportData(res.data);
        } catch {
          setReportData(stateData);
        } finally {
          setLoading(false);
        }
      };
      linkAndLoad();
      return;
    }

    if (stateData) {
      setReportData(stateData);
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await getVisibilityReport(visibility_report_id);
        setReportData(res.data);
      } catch {
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [visibility_report_id, location.state]);

  const openAuthInPlace = (mode) => {
    navigate(`${location.pathname}?auth=${mode}`);
  };

  const toggleProvider = (key) => {
    setExpandedProviders((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading visibility report..." />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-on-surface-variant text-lg mb-4">Report not found</p>
          <button
            onClick={() => navigate("/llm-visibility")}
            className="px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-surface rounded-lg font-medium hover:opacity-90 transition-all"
          >
            Run New Check
          </button>
        </div>
      </div>
    );
  }

  const {
    query,
    llm_provider_count,
    total_entities_found,
    entities,
    inferred_entity_label,
    raw_extraction,
    user_entity,
    explanation,
  } = reportData;
  const llmProviders = Object.keys(raw_extraction || {});
  const userEntityName = user_entity?.found ? user_entity?.entity?.name : null;
  const entityNoun = inferred_entity_label || "result";
  const entityNounPlural = pluralize(entityNoun);

  const openBusinessProfile = () => window.dispatchEvent(new CustomEvent("openBusinessProfile"));

  /* ── Lock screen ───────────────────────────────────────────────────── */
  const renderLockScreen = () => (
    <div className="text-center py-24">
      <Lock className="w-16 h-16 text-on-surface-variant mx-auto mb-6" />
      <div className="text-2xl font-bold text-on-surface-variant mb-4">Login Required</div>
      <p className="text-on-surface-variant mb-6 max-w-md mx-auto">
        To see all other {entityNounPlural} that came up, login to Unlock
      </p>
      <div className="space-y-3">
        <button
          onClick={() => openAuthInPlace("signup")}
          className="block w-full max-w-xs mx-auto px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-surface rounded-lg hover:opacity-90 transition-all font-medium"
        >
          Sign Up to Unlock
        </button>
        <button
          onClick={() => openAuthInPlace("login")}
          className="text-sm text-primary hover:opacity-80 transition-colors"
        >
          Already have an account? Log in
        </button>
      </div>
    </div>
  );

  /* ── Entity Card ───────────────────────────────────────────────────── */
  const renderEntityCard = (entity, rank, blurred = false, isYou = false) => {
    const attribution = explanation?.businesses?.find((b) => b.name === entity.name)?.attribution;

    return (
      <div
        key={entity.name}
        className={`p-5 rounded-xl border bg-surface-container-lowest ${
          isYou ? "border-primary ring-2 ring-primary/40" : "border-surface-container-high"
        } ${blurred ? "blur-[8px] select-none pointer-events-none" : ""}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="text-2xl font-bold text-on-surface-variant shrink-0">#{rank}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-on-surface truncate">{entity.name}</h3>
                {isYou && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30 shrink-0">
                    You
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <p className="text-sm text-on-surface-variant">
                  Mentioned by {entity.mentioned_by.length}/{llm_provider_count} LLMs
                </p>
                {attribution && <VisibilityAttributionBadge attribution={attribution} compact />}
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className={`text-2xl font-bold ${scoreTextColor(entity.total_score)}`}>
              {entity.total_score}
            </span>
            <p className="text-xs text-on-surface-variant">/ 100</p>
          </div>
        </div>

        {/* Score bar */}
        <div className="mt-4 h-2 bg-surface-container-low rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${scoreColor(entity.total_score)}`}
            style={{ width: `${entity.total_score}%` }}
          />
        </div>

        {/* LLM Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(LLM_COLORS).map(([key, style]) => {
            const mentioned = entity.mentioned_by.includes(key);
            return (
              <span
                key={key}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${
                  mentioned
                    ? `${style.bg} ${style.text} ${style.border}`
                    : "bg-surface-container-low text-on-surface-variant border-surface-container-high"
                }`}
              >
                {mentioned ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {style.label}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── Your Business Panel ───────────────────────────────────────────── */
  const renderYourEntityPanel = () => {
    // Anonymous user or no business profile → subtle nudge to complete profile.
    if (!user_entity) {
      return (
        <button
          onClick={openBusinessProfile}
          className="w-full flex items-center gap-3 p-4 rounded-xl border border-surface-container-high bg-surface-container-low text-left hover:bg-surface-container-high transition-colors"
        >
          <Info className="w-5 h-5 text-primary shrink-0" />
          <span className="text-sm text-on-surface-variant">
            Complete your business profile to see whether{" "}
            <span className="text-on-surface font-medium">your business</span> ranks in these
            results.
          </span>
        </button>
      );
    }

    // Profile complete but business did not appear → amber "not visible" banner.
    if (!user_entity.found) {
      return (
        <div className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-on-surface font-semibold">
                {user_entity.business_name
                  ? `${user_entity.business_name} didn't appear in LLM results for this query yet`
                  : "Your business didn't appear in LLM results for this query yet"}
              </p>
              <p className="text-sm text-on-surface-variant mt-1">
                Only a handful of {entityNounPlural} surface in AI search. Improving your visibility
                — schema markup, reviews, and direct SEO — can help you break into these results.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Business found → highlighted "Your Business" card.
    const { entity, rank, business_name } = user_entity;
    return (
      <div className="p-5 rounded-xl border border-primary ring-2 ring-primary/30 bg-primary/5">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-primary" />
          <span className="text-sm font-bold uppercase tracking-wider text-primary">
            Your Business
          </span>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="text-2xl font-bold text-primary shrink-0">#{rank}</span>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-on-surface truncate">
                {business_name || entity.name}
              </h3>
              <p className="text-sm text-on-surface-variant mt-1">
                Mentioned by {entity.mentioned_by.length}/{llm_provider_count} LLMs
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className={`text-2xl font-bold ${scoreTextColor(entity.total_score)}`}>
              {entity.total_score}
            </span>
            <p className="text-xs text-on-surface-variant">/ 100</p>
          </div>
        </div>

        {/* Score bar */}
        <div className="mt-4 h-2 bg-surface-container-low rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${scoreColor(entity.total_score)}`}
            style={{ width: `${entity.total_score}%` }}
          />
        </div>

        {/* LLM Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(LLM_COLORS).map(([key, style]) => {
            const mentioned = entity.mentioned_by.includes(key);
            return (
              <span
                key={key}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${
                  mentioned
                    ? `${style.bg} ${style.text} ${style.border}`
                    : "bg-surface-container-low text-on-surface-variant border-surface-container-high"
                }`}
              >
                {mentioned ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {style.label}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── Overview Tab ──────────────────────────────────────────────────── */
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-6 rounded-xl border border-surface-container-high bg-surface-container-lowest">
        <div className="flex items-center gap-3 mb-3">
          <Search className="w-5 h-5 text-primary" />
          <span className="text-on-surface-variant text-sm">Query</span>
        </div>
        <p className="text-on-surface text-lg font-medium">"{query}"</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {Object.entries(LLM_COLORS).map(([key, style]) => (
            <span
              key={key}
              className={`text-xs font-semibold px-3 py-1 rounded-full border ${style.bg} ${style.text} ${style.border}`}
            >
              {style.label}
            </span>
          ))}
          <span className="text-xs font-semibold px-3 py-1 rounded-full border border-surface-container-high bg-surface-container-low text-on-surface-variant">
            {total_entities_found} {entityNounPlural} found
          </span>
        </div>
      </div>

      {/* Your Business panel */}
      {renderYourEntityPanel()}

      {/* Entities */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-on-surface">
            {capitalize(entityNounPlural)} Leaderboard
          </h3>
          <button
            onClick={() => setShowScoreInfo(!showScoreInfo)}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
            title="What does the score mean?"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>

        {/* Score info tooltip */}
        {showScoreInfo && (
          <div className="p-4 rounded-xl border border-surface-container-high bg-surface-container-low text-sm text-on-surface-variant">
            Score is calculated based on how often a {entityNoun} appears across LLM providers
            (visibility) and how high it ranks in each response (ranking). Higher visibility +
            higher ranking = higher score.
          </div>
        )}

        {entities.map((entity, i) =>
          renderEntityCard(
            entity,
            i + 1,
            false,
            !!userEntityName && entity.name === userEntityName,
          ),
        )}

        {/* Blurred placeholder for anonymous users */}
        {!loggedIn && (
          <>
            {renderEntityCard(
              {
                name: `Hidden ${capitalize(entityNoun)} Result`,
                mentioned_by: ["chatgpt", "gemini"],
                total_score: 62.5,
                rank_position: {},
              },
              2,
              true,
            )}

            {/* CTA */}
            <div className="p-6 rounded-xl border border-primary/20 bg-primary/5 text-center">
              <Lock className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-on-surface font-semibold mb-2">
                To see all other {entityNounPlural} that came up, login to Unlock
              </p>
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => openAuthInPlace("signup")}
                  className="px-6 py-2.5 bg-gradient-to-br from-primary to-primary-container text-on-surface rounded-lg font-medium hover:opacity-90 transition-all"
                >
                  Sign Up to Unlock
                </button>
                <button
                  onClick={() => openAuthInPlace("login")}
                  className="text-sm text-primary hover:opacity-80 transition-colors"
                >
                  Already have an account? Log in
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Score Legend */}
      <div className="p-6 rounded-xl border border-surface-container-high bg-surface-container-lowest">
        <h4 className="text-sm font-bold text-on-surface mb-4">What does the score mean?</h4>
        <div className="space-y-3">
          {SCORE_LEGEND.map((item) => (
            <div key={item.range} className="flex items-start gap-3">
              <span className={`w-3 h-3 rounded-sm mt-1 shrink-0 ${item.color}`} />
              <div>
                <span className="text-sm font-semibold text-on-surface">
                  {item.range} — {item.label}
                </span>
                <span className="text-sm text-on-surface-variant ml-2">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ── Full Report Tab ───────────────────────────────────────────────── */
  const renderFullReport = () => {
    if (!loggedIn) return renderLockScreen();

    return (
      <div className="space-y-8">
        {/* Score & Rank Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-container-high">
                <th className="text-left py-3 px-4 text-on-surface-variant font-medium">
                  {capitalize(entityNoun)}
                </th>
                <th className="text-center py-3 px-4 text-on-surface-variant font-medium">Score</th>
                <th className="text-center py-3 px-4 text-on-surface-variant font-medium">
                  Mentioned By
                </th>
                {llmProviders.map((key) => (
                  <th
                    key={key}
                    className="text-center py-3 px-4 text-on-surface-variant font-medium"
                  >
                    {LLM_COLORS[key]?.label || key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entities.map((entity) => {
                const isYou = !!userEntityName && entity.name === userEntityName;
                return (
                  <tr
                    key={entity.name}
                    className={`border-b border-surface-container-high hover:bg-surface-container-low ${
                      isYou ? "bg-primary/5" : ""
                    }`}
                  >
                    <td className="py-3 px-4 text-on-surface font-medium">
                      <span className="flex items-center gap-2">
                        {entity.name}
                        {isYou && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30">
                            You
                          </span>
                        )}
                      </span>
                    </td>
                    <td
                      className={`py-3 px-4 text-center font-bold ${scoreTextColor(entity.total_score)}`}
                    >
                      {entity.total_score}
                    </td>
                    <td className="py-3 px-4 text-center text-on-surface-variant">
                      {entity.mentioned_by.length}/{llm_provider_count}
                    </td>
                    {llmProviders.map((key) => {
                      const rank = entity.rank_position?.[key];
                      return (
                        <td key={key} className="py-3 px-4 text-center">
                          {rank ? (
                            <span
                              className={`font-semibold ${LLM_COLORS[key]?.text || "text-on-surface"}`}
                            >
                              #{rank}
                            </span>
                          ) : (
                            <span className="text-on-surface-variant">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Entities per LLM Provider (Accordions) */}
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-on-surface">
            {capitalize(entityNounPlural)} Listed Per LLM Provider
          </h3>
          {llmProviders.map((key) => {
            const style = LLM_COLORS[key] || {
              bg: "bg-surface-container-low",
              text: "text-on-surface",
              border: "border-surface-container-high",
              label: key,
            };
            const entityList = raw_extraction[key] || [];
            const isExpanded = expandedProviders[key];
            const providerEvidence = explanation?.providers?.find((p) => p.llm_provider === key);

            return (
              <div
                key={key}
                className="rounded-xl border border-surface-container-high overflow-hidden"
              >
                <button
                  onClick={() => toggleProvider(key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-surface-container-low transition-colors"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className={`text-sm font-semibold px-3 py-1 rounded-full border ${style.bg} ${style.text} ${style.border}`}
                    >
                      {style.label}
                    </span>
                    <span className="text-on-surface-variant text-sm">
                      {entityList.length} {entityNounPlural}
                    </span>
                    {providerEvidence && (
                      <span className="text-xs text-on-surface-variant italic">
                        {providerEvidence.searched_web
                          ? `searched the web — cited ${providerEvidence.cited_source_count} source${providerEvidence.cited_source_count === 1 ? "" : "s"}`
                          : "answered from memory — cited no sources"}
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-on-surface-variant" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-on-surface-variant" />
                  )}
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2">
                    {entityList.map((name, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 py-2 px-3 rounded-lg bg-surface-container-low"
                      >
                        <span className="text-sm font-bold text-on-surface-variant w-6">
                          #{idx + 1}
                        </span>
                        <span className="text-sm text-on-surface">{name}</span>
                      </div>
                    ))}
                    {entityList.length === 0 && (
                      <p className="text-sm text-on-surface-variant italic">
                        No {entityNounPlural} extracted
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Scoring Methodology */}
        <div className="p-4 rounded-xl border border-surface-container-high bg-surface-container-lowest text-sm text-on-surface-variant">
          Scores are calculated based on visibility across LLM providers and ranking in their
          responses.
        </div>
      </div>
    );
  };

  /* ── Why These Results Tab ─────────────────────────────────────────── */
  const renderWhyResults = () => {
    if (!loggedIn) return renderLockScreen();

    return <VisibilityWhyPanel explanation={explanation} userBusinessName={userEntityName} />;
  };

  /* ── Main render ───────────────────────────────────────────────────── */
  const tabs = ["Overview", "Full Report", "Why These Results"];

  const tabContent = {
    Overview: renderOverview,
    "Full Report": renderFullReport,
    "Why These Results": renderWhyResults,
  };

  return (
    <div className="min-h-screen pt-10 pb-16 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/llm-visibility")}
            className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            New report
          </button>
          {loggedIn && (
            <DownloadPdfButton reportType="llm_visibility" reportId={visibility_report_id} />
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex gap-1 p-1 bg-surface-container-low rounded-xl border border-surface-container-high overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap flex-1 py-3 px-5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === tab
                    ? "bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-lg"
                    : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high"
                }`}
              >
                {tab}
                {tab !== "Overview" && !loggedIn && (
                  <Lock className="w-3.5 h-3.5 inline ml-2 opacity-70" />
                )}
              </button>
            ))}
          </div>
          {loggedIn && (
            <button
              onClick={() => navigate(`/trends?type=${TREND_REPORT_TYPE.LLM_VISIBILITY}`)}
              title="View Trends"
              aria-label="View Trends"
              className="shrink-0 p-2 text-on-surface bg-surface-container-low border border-surface-container-high hover:bg-surface-container-high rounded-xl transition-colors flex items-center justify-center"
            >
              <TrendingUp className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tab content */}
        {tabContent[activeTab]?.()}

        <CrossReportBanner currentReport="visibility" />
      </div>
    </div>
  );
};

export default VisibilityReport;
